import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { Popup } from '../../../../../core/shared/components/popup/popup';
import { ToastService } from '../../../../../core/shared/services/toast.service';
import { CraditService } from '../../services/cradit.service';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { RichTextEditor } from '../rich-text-editor/rich-text-editor';
import { SectionDragHandle } from '../section-drag-handle/section-drag-handle';
import { RichTextPipe } from '../../utils/rich-text.pipe';
import { TableCellPipe } from '../../utils/table-cell.pipe';
import { richTextForSave } from '../../utils/rich-text.util';
import { moveVisibleItem } from '../../utils/section-reorder.util';
import {
  ReportBlock,
  ReportTable,
  ReportTableColumn,
} from '../../models/report-block.model';

@Component({
  selector: 'app-financial-performance-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
    RichTextEditor,
    RichTextPipe,
    TableCellPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle,
  ],
  templateUrl: './financial-performance-section.html',
  styleUrl: './financial-performance-section.css',
})
export class FinancialPerformanceSection
  implements OnInit, OnChanges, OnDestroy
{
  @Input() sectionId = '';
  @Input() editable = false;
  @Input() refGrp = 28;
  @Input() refValSeq!: number;

  Financial = 2527;

  blocks: ReportBlock[] = [];

  saving = false;
  errorMessage = '';
  successMessage = '';

  onboardingId!: number;

  crdtPrpslSeq: number | null = null;
  crdtPrpslBlkSeq: number | null = null;

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteIndex: number | null = null;

  private saveSub?: Subscription;
  private seqSub?: Subscription;
  private initialized = false;

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onboardingId = Number(
      this.route.snapshot.paramMap.get('id') ||
        this.route.parent?.snapshot.paramMap.get('oid'),
    );

    this.initialized = true;

    this.seqSub = this.creditService.crdtPrpslSeq$.subscribe((seq) => {
      this.crdtPrpslSeq = seq;
      this.loadSection();
    });

    this.saveSub = this.creditService.saveSectionTrigger$.subscribe(
      (sectionId) => {
        if (sectionId === this.sectionId) {
          this.saveSection();
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sectionChanged =
      changes['sectionId'] ||
      changes['refValSeq'] ||
      changes['refGrp'];

    if (!this.initialized || !sectionChanged) {
      return;
    }

    this.blocks = [];
    this.crdtPrpslBlkSeq = null;

    this.loadSection();
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.seqSub?.unsubscribe();
  }

  get activeBlocks(): ReportBlock[] {
    return this.blocks.filter((block) => block.delFlg !== 1);
  }

  /** Object identity, so a block's DOM moves with it when reordered. */
  trackByBlock(_index: number, block: ReportBlock): ReportBlock {
    return block;
  }

  /** Drag-and-drop reorder; blocks are saved in array order. */
  onBlockDrop(event: CdkDragDrop<unknown>): void {
    this.blocks = moveVisibleItem(
      this.blocks,
      (block) => block.delFlg !== 1,
      event.previousIndex,
      event.currentIndex,
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  private ensureTable(block: ReportBlock): ReportTable {
    if (!block.table) {
      block.table = {
        columns: [],
        rows: [],
      };
    }

    return block.table;
  }

  private normalizeTable(table: unknown): ReportTable {
    const source = table as {
      columns?: unknown;
      rows?: unknown;
    };

    const rawColumns = Array.isArray(source?.columns)
      ? source.columns
      : [];

    const columns: ReportTableColumn[] = rawColumns.map((column) => {
      // Supports old saved data: columns: ['Name', 'Amount']
      if (typeof column === 'string') {
        return {
          title: column,
          subColumns: [],
        };
      }

      const value = column as {
        title?: unknown;
        subColumns?: unknown;
      };

      return {
        title: typeof value?.title === 'string' ? value.title : '',
        subColumns: Array.isArray(value?.subColumns)
          ? value.subColumns.map((subColumn) => String(subColumn ?? ''))
          : [],
      };
    });

    const leafColumnCount = columns.reduce(
      (total, column) => total + this.getColumnLeafCount(column),
      0,
    );

    const rawRows = Array.isArray(source?.rows) ? source.rows : [];

    const rows: string[][] = rawRows.map((row) => {
      const values = Array.isArray(row)
        ? row.map((cell) => String(cell ?? ''))
        : [];

      while (values.length < leafColumnCount) {
        values.push('');
      }

      return values.slice(0, leafColumnCount);
    });

    return {
      columns,
      rows,
    };
  }

  loadSection(): void {
    if (!this.crdtPrpslSeq || !this.refValSeq) {
      this.blocks = [];
      return;
    }

    const requestedSectionId = this.sectionId;
    const requestedRefValSeq = this.refValSeq;

    this.creditService
      .getCrdtBlck(this.crdtPrpslSeq, [requestedRefValSeq])
      .subscribe({
        next: (res: any[]) => {
          if (
            requestedSectionId !== this.sectionId ||
            requestedRefValSeq !== this.refValSeq
          ) {
            return;
          }

          if (!res?.length) {
            this.blocks = [];
            return;
          }

          const row = res[0];

          this.crdtPrpslBlkSeq = row?.crdtPrpslBlkSeq ?? null;

          const savedBlocks = row?.jsonData?.[0]?.blocks;

          if (!Array.isArray(savedBlocks)) {
            this.blocks = [];
            return;
          }

          this.blocks = savedBlocks.map((block: ReportBlock) => ({
            type: block.type,
            title: block.title ?? '',
            content: block.content ?? '',
            expanded: false,
            delFlg: block.delFlg ?? 0,
            table:
              block.type === 'table'
                ? this.normalizeTable(block.table)
                : undefined,
          }));
        },

        error: (error) => {
          console.error('Load section error:', error);
          this.blocks = [];
        },
      });
  }

  saveSection(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.saving = true;

    const cleanedBlocks = this.blocks.map((block) => ({
      type: block.type,
      title: block.title?.trim() || '',
      content: block.type === 'paragraph' ? richTextForSave(block.content) : block.content || '',
      delFlg: block.delFlg ?? 0,
      table:
        block.type === 'table'
          ? this.normalizeTable(block.table)
          : null,
    }));

    const payload = {
      crprtOnbrdngSeq: this.onboardingId,
      crdtPrpslSeq: this.crdtPrpslSeq,

      blockList: [
        {
          crdtPrpslBlkSeq: this.crdtPrpslBlkSeq ?? null,
          refGrp: this.refGrp,
          refValSeq: this.refValSeq,
          blkTyp: 3,
          delFlg: 0,
          sortOrdr: 1,
          txtData: '',
          jsonData: [
            {
              blocks: cleanedBlocks,
            },
          ],
        },
      ],
    };

    this.creditService.saveSection(payload).subscribe({
      next: (res: any) => {
        this.saving = false;
        this.successMessage = this.translate.instant('CREDIT_SECTION_FIN_PERF.SAVED');

        this.toast.success(this.successMessage);

        this.creditService.setCrdtPrpslSeq(res?.crdtPrpslSeq);

        this.crdtPrpslBlkSeq =
          res?.crdtPrpslBlkSeq ?? this.crdtPrpslBlkSeq;

        this.loadSection();

        this.creditService.reportSectionSaveResult({
          sectionId: this.sectionId,
          status: 'saved',
        });
      },

      error: (error) => {
        this.saving = false;

        console.error('Save section error:', error);

        this.errorMessage =
          error?.error?.message ?? this.translate.instant('CREDIT_SECTION_FIN_PERF.SAVE_FAILED');

        this.creditService.reportSectionSaveResult({
          sectionId: this.sectionId,
          status: 'api-error',
          message: this.errorMessage,
        });
      },
    });
  }

  addHeading(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'heading',
        title: '',
        expanded: true,
        delFlg: 0,
      },
    ];
  }

  addParagraph(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'paragraph',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,
      },
    ];
  }

  addBulletList(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'bullet-list',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,
      },
    ];
  }

  addTable(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'table',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,
        table: {
          columns: [],
          rows: [],
        },
      },
    ];
  }

  requestRemoveBlock(index: number): void {
    const selectedBlock = this.activeBlocks[index];

    if (!selectedBlock) {
      return;
    }

    this.pendingDeleteIndex = index;
    const blockTypeKeys: Record<string, string> = {
      heading: 'CREDIT_SECTION_FIN_PERF.BLOCK_TYPE_HEADING',
      paragraph: 'CREDIT_SECTION_FIN_PERF.BLOCK_TYPE_NARRATIVE',
      'bullet-list': 'CREDIT_SECTION_FIN_PERF.BLOCK_TYPE_BULLET_LIST',
      table: 'CREDIT_SECTION_FIN_PERF.BLOCK_TYPE_TABLE',
    };
    const blockTypeKey = blockTypeKeys[selectedBlock.type];
    this.deleteMessage = this.translate.instant('CREDIT_SECTION_FIN_PERF.DELETE_BLOCK_CONFIRM', {
      type: blockTypeKey ? this.translate.instant(blockTypeKey) : selectedBlock.type,
    });
    this.showDeletePopup = true;
  }

  confirmRemoveBlock(): void {
    if (this.pendingDeleteIndex === null) {
      return;
    }

    this.removeBlock(this.pendingDeleteIndex);
    this.pendingDeleteIndex = null;

    this.toast.success('CREDIT_SECTION_FIN_PERF.BLOCK_DELETED');
  }

  private removeBlock(index: number): void {
    const selectedBlock = this.activeBlocks[index];

    if (!selectedBlock) {
      return;
    }

    selectedBlock.delFlg = 1;
    selectedBlock.expanded = false;
  }

  getColumnLeafCount(column: ReportTableColumn): number {
    return column.subColumns.length || 1;
  }

 getLeafColumns(
  block: ReportBlock,
): Array<{ headerIndex: number; subIndex: number | null }> {
  const leafColumns: Array<{
    headerIndex: number;
    subIndex: number | null;
  }> = [];

  if (!block.table) {
    return leafColumns;
  }

  block.table.columns.forEach((header, headerIndex) => {
    if (header.subColumns.length > 0) {
      header.subColumns.forEach((_subColumn, subIndex) => {
        leafColumns.push({
          headerIndex,
          subIndex,
        });
      });

      return;
    }

    leafColumns.push({
      headerIndex,
      subIndex: null,
    });
  });

  return leafColumns;
}

  hasAnySubColumns(block: ReportBlock): boolean {
    return !!block.table?.columns.some(
      (header) => header.subColumns.length > 0,
    );
  }

  getHeaderRowSpan(
    block: ReportBlock,
    header: ReportTableColumn,
  ): number {
    return this.hasAnySubColumns(block) && header.subColumns.length === 0
      ? 2
      : 1;
  }

  private getHeaderStartIndex(
    block: ReportBlock,
    headerIndex: number,
  ): number {
    if (!block.table) {
      return 0;
    }

    return block.table.columns
      .slice(0, headerIndex)
      .reduce(
        (total, header) => total + this.getColumnLeafCount(header),
        0,
      );
  }

  addHeader(block: ReportBlock): void {
    const table = this.ensureTable(block);

    table.columns.push({
      title: '',
      subColumns: [],
    });

    table.rows.forEach((row) => {
      row.push('');
    });
  }

  removeHeader(block: ReportBlock, headerIndex: number): void {
    if (!block.table) {
      return;
    }

    const header = block.table.columns[headerIndex];

    if (!header) {
      return;
    }

    const startIndex = this.getHeaderStartIndex(block, headerIndex);
    const removeCount = this.getColumnLeafCount(header);

    block.table.columns.splice(headerIndex, 1);

    block.table.rows.forEach((row) => {
      row.splice(startIndex, removeCount);
    });
  }

  addSubColumn(block: ReportBlock, headerIndex: number): void {
    if (!block.table) {
      return;
    }

    const header = block.table.columns[headerIndex];

    if (!header) {
      return;
    }

    // The first sub-column uses the existing data column.
    if (header.subColumns.length === 0) {
      header.subColumns.push('');
      return;
    }

    const insertAt =
      this.getHeaderStartIndex(block, headerIndex) +
      header.subColumns.length;

    header.subColumns.push('');

    block.table.rows.forEach((row) => {
      row.splice(insertAt, 0, '');
    });
  }

  removeSubColumn(
    block: ReportBlock,
    headerIndex: number,
    subIndex: number,
  ): void {
    if (!block.table) {
      return;
    }

    const header = block.table.columns[headerIndex];

    if (!header || header.subColumns.length === 0) {
      return;
    }

    // Removing the only sub-column returns the header to a normal column.
    if (header.subColumns.length === 1) {
      header.subColumns = [];
      return;
    }

    const removeAt =
      this.getHeaderStartIndex(block, headerIndex) + subIndex;

    header.subColumns.splice(subIndex, 1);

    block.table.rows.forEach((row) => {
      row.splice(removeAt, 1);
    });
  }

  addRow(block: ReportBlock): void {
    const table = this.ensureTable(block);
    const columnCount = this.getLeafColumns(block).length;

    if (!columnCount) {
      return;
    }

    table.rows.push(
      Array.from({ length: columnCount }, () => ''),
    );
  }

  removeRow(block: ReportBlock, rowIndex: number): void {
    block.table?.rows.splice(rowIndex, 1);
  }

  getLines(content = ''): string[] {
    return content
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);
  }
}
